import BaseService from './BaseService'
import axios from 'axios'
import config from '../config'

class IntegrationOauthService extends BaseService {
  constructor() {
    super(config.restApiUrl, true)
  }

  // Method to fetch integration details
  async fetchIntegrationDetails(appId: string) {
    try {
      if (!appId) {
        return null
      }

      const response = await this.requests.get(`/integrations/public/${appId}`)
      return response?.integration || null
    } catch (error) {
      console.error('Error fetching integration details:', error)
      return null
    }
  }

  // Method to initiate OAuth authorization
  async initiateOAuthAuthorization(params: {
    appId: string
    locationId: string
    companyId?: string
  }): Promise<boolean> {
    const { appId, locationId, companyId } = params

    if (!appId || !locationId) {
      console.error('Missing required parameters for OAuth authorization')
      return false
    }

    try {
      // First, fetch integration details to get the client ID
      const integrationDetails = await this.fetchIntegrationDetails(appId)
      if (
        !integrationDetails ||
        !integrationDetails.clientKeys ||
        integrationDetails.clientKeys.length === 0
      ) {
        console.error('No client keys found for integration')
        return false
      }

      const clientId = integrationDetails.clientKeys[0].id

      // Make the POST request to initiate OAuth authorization
      const response = await this.requests.post(
        `/workflows-marketplace/integration/${appId}/install?locationId=${locationId}`,
        {
          clientId,
          companyId,
        }
      )

      return true
    } catch (error) {
      console.error('Error initiating OAuth authorization:', error)
      return false
    }
  }

  // Method to fetch OAuth URL for the app
  async fetchOAuthUrl(params: {
    appId: string
    locationId: string
  }): Promise<string | null> {
    const { appId, locationId } = params

    if (!appId || !locationId) {
      console.error('Missing required parameters for fetching OAuth URL')
      return null
    }

    try {
      const url = `${config.restApiUrl}/oauth/clients/${appId}/authentication/oauth2/location/${locationId}/url`

      const response = await axios.get(url, {
        headers: this.headers,
      })

      console.log('OAuth URL fetched successfully:', response)
      return response?.data?.url || null
    } catch (error) {
      console.error('Error fetching OAuth URL:', error)
      return null
    }
  }

  // Method to invalidate cache for an integration
  invalidateCache(appId: string, locationId: string): void {
    // This method can be used to clear any cached data for the integration
    // Implementation depends on your caching strategy
    console.log(`Invalidating cache for app ${appId} and location ${locationId}`)
  }

  // Helper method to get app ID from app name
  async getAppId(appName: string): Promise<string | null> {
    try {
      const response = await this.requests.get(`/integrations/public/search`, {
        params: { name: appName }
      })
      return response?.integration?.id || null
    } catch (error) {
      console.error('Error fetching app ID:', error)
      return null
    }
  }

  // Method to check connection status
  async checkConnectionStatus(params: {
    appId: string
    locationId: string
    companyId: string
  }): Promise<{ isConnected: boolean; validToken?: boolean; isDisconnected?: boolean }> {
    const { appId, locationId, companyId } = params

    if (!appId || !locationId || !companyId) {
      console.error('Missing required parameters for checking connection status')
      return { isConnected: false, isDisconnected: false }
    }

    try {
      const params = {
        limit: 1,
        skip: 0,
        companyId,
        appId,
        isInstalled: true,
        locationId
      }

      const response = await this.requests.get("/oauth/installedLocations", { params })

          if (response?.currentLocation && response?.currentLocation?.externalAuthDetails) {
      const integration = response?.currentLocation?.externalAuthDetails || {}

      // Check if integration is installed but externalAuthDetails is empty
      if ((!integration || Object.keys(integration).length === 0)) {
        console.log('Integration is installed but externalAuthDetails is empty - disconnected state')
        return { isConnected: false, isDisconnected: true }
      }

      // Check if integration is installed and has externalAuthDetails
      if (integration.validToken) {
        return { isConnected: true, validToken: true, isDisconnected: false }
      }

        return { isConnected: false, isDisconnected: false }
      } else {
        return { isConnected: false, isDisconnected: false }
      }
    } catch (error) {
      console.error('Error checking connection status:', error)
      return { isConnected: false, isDisconnected: false }
    }
  }

  // Method to disconnect integration
  async disconnectIntegration(params: {
    appId: string
    locationId: string
    companyId: string
  }): Promise<void> {
    const { appId, locationId, companyId } = params

    if (!appId || !locationId || !companyId) {
      throw new Error('Missing required parameters for disconnecting integration')
    }

    const body = {
      companyId: companyId,
      approvedLocations: [
        locationId
      ],
      excludedLocations: [],
      approveAllLocations: false,
      unInstallAgency: false
    }

    await this.requests.delete(`/marketplace/app/${appId}/installations`, {}, body)
  }

  // Method to fetch integration apps from workflows-marketplace
  async fetchIntegrationApps(locationId: string): Promise<any[]> {
    if (!locationId) {
      console.error('Missing locationId for fetching integration apps')
      return []
    }

    try {
      const response = await this.requests.get(`/workflows-marketplace/integration-apps`, {
        params: { locationId }
      })

      return response || []
    } catch (error) {
      console.error('Error fetching integration apps:', error)
      return []
    }
  }
}

const instance = new IntegrationOauthService()

export default instance
